import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const ProductsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    document.title = '삼원테크 | 제품소개 - STS패널형·원통형·라이닝 물탱크';
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      '삼원테크 물탱크 제품소개. STS패널형(3~250톤), STS원통형(3~500톤), 라이닝 물탱크. 스테인리스 소재 직접 생산, ISO9001 인증 품질 보증.'
    );
    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "삼원테크 물탱크 제품 목록",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products`,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Product",
            "name": "STS패널형 물탱크",
            "description": "조립식 구조로 현장 설치가 용이하며, 대용량 저수 시설에 최적화된 스테인리스 물탱크",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/process/sts-panel`,
            "brand": { "@type": "Brand", "name": "삼원테크" },
            "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "priceCurrency": "KRW" }
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "Product",
            "name": "STS원통형 물탱크",
            "description": "일체형 구조로 내구성이 뛰어나며, 중소규모 저수 시설에 적합한 스테인리스 물탱크",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/process/sts-cylinder`,
            "brand": { "@type": "Brand", "name": "삼원테크" },
            "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "priceCurrency": "KRW" }
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "Product",
            "name": "라이닝 물탱크",
            "description": "특수 코팅 처리로 부식 방지 및 수질 보호 기능이 강화된 물탱크",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/process/lining`,
            "brand": { "@type": "Brand", "name": "삼원테크" },
            "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "priceCurrency": "KRW" }
          }
        }
      ]
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-products';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-products')?.remove(); };
  }, []);

  const categories = [
    { id: 'all', label: '전체' },
    { id: 'sts-panel', label: 'STS패널형' },
    { id: 'sts-cylinder', label: 'STS원통형' },
    { id: 'lining', label: '라이닝' },
  ];

  const products = [
    {
      id: 1,
      category: 'sts-panel',
      name: 'STS패널형 물탱크',
      description: '용접식 구조로 현장 설치가 용이하며, 대용량 저수 시설에 최적화된 제품입니다.',
      capacity: '3톤 ~',
      features: ['용접식 구조', '현장 맞춤 제작', '내식성 우수', '위생적'],
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/2d576a2c5bd3ef0148d6945391d8aaab.png',
      link: '/process/sts-panel'
    },
    {
      id: 2,
      category: 'sts-cylinder',
      name: 'STS원통형 물탱크',
      description: '일체형 구조로 내구성이 뛰어난 제품입니다.',
      capacity: '3톤 ~',
      features: ['일체형 구조', '고강도', '장기 내구성', '유지보수 용이'],
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/a622e2ab3f4ddef9675dcd029ea73e5d.jpeg',
      link: '/process/sts-cylinder'
    },
    {
      id: 3,
      category: 'lining',
      name: '라이닝 물탱크',
      description: '에폭시 코팅 처리로 부식 방지 및 수질 보호 기능이 강화된 제품입니다.',
      capacity: ' ',
      features: ['특수 코팅', '부식 방지', '수질 보호', '경제적'],
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/752fcbc80e86976d105c29f7c58f6f6b.png',
      link: '/process/lining'
    },
  ];

  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 text-white py-24">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
                제품소개
              </h1>
              <p className="text-xl md:text-2xl text-teal-50 max-w-3xl mx-auto leading-relaxed">
                삼원테크의 다양한 물탱크 제품을 만나보세요
              </p>
            </motion.div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="bg-gray-50 py-8 sticky top-20 z-30 border-b border-gray-200">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCategory === category.id
                      ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30'
                      : 'bg-white text-gray-700 hover:bg-teal-50 hover:text-teal-600 border border-gray-200'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProducts.map((product) => (
                <motion.div
                  key={product.id}
                  variants={itemVariants}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 group"
                >
                  <div className="relative w-full h-64 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors duration-300">
                      {product.name}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 leading-relaxed text-sm whitespace-nowrap">
                      {product.description}
                    </p>
                    
                    <div className="mb-4 pb-4 border-b border-gray-100">
                      {(product.capacity !== undefined) && (
                        <div className="flex items-center gap-2 text-teal-600">
                          <i className="ri-water-flash-line text-xl"></i>
                          <span className="font-semibold text-sm">용량: {product.capacity.trim()}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">주요 특징</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {product.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <i className="ri-checkbox-circle-fill text-teal-500 text-base"></i>
                            <span className="text-xs text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <Link
                      to={product.link}
                      className="block w-full bg-teal-600 hover:bg-teal-700 text-white text-center py-3 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-xl whitespace-nowrap cursor-pointer"
                    >
                      제작과정 보기
                    </Link>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-teal-50 via-white to-teal-50">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 rounded-3xl p-12 md:p-16 text-center text-white shadow-2xl">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                맞춤형 물탱크 제작 상담
              </h2>
              <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto leading-relaxed">
                고객님의 요구사항에 맞는 최적의 물탱크 솔루션을 제안해드립니다
              </p>
              <Link
                to="/quote"
                className="inline-block bg-white text-teal-600 px-10 py-4 rounded-full font-bold text-lg hover:bg-teal-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                상담 문의하기
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProductsPage;
